/**
 * Diagnostics seams, and the one rule that keeps them out of a family's way.
 *
 * The stage has carried load-bearing diagnostic surfaces since Phase 1 (the input feed, the asset
 * report, the narration readout — D-040(3)): they exist because a behaviour that leaves no trace
 * cannot be verified, and they are for grown-ups, never for a child to act on.
 *
 * The synthetic-pose driver is the same class of thing and a stronger one, because it can *produce*
 * input rather than only report it. So it is gated twice over: it is installed only when somebody
 * asks for it by name in the URL, and only on a loopback origin — the dev server and the local
 * preview the end-to-end suite runs against. A deployed Thrive Games has no way to switch it on.
 */

/** Ask for the driver with `?syntheticPose=1`. Named, not guessable by accident. */
export const SYNTHETIC_POSE_PARAM = 'syntheticPose';

/** Where a local build is served from. Anything else is somebody else's origin. */
const LOOPBACK_HOSTS: readonly string[] = ['localhost', '127.0.0.1', '[::1]', '::1'];

export interface SyntheticPoseGateInput {
  readonly hostname: string;
  readonly search: string;
}

/**
 * Whether the synthetic-pose driver may be installed for this page load.
 *
 * Both halves are required. The parameter alone would ship a working input injector to every
 * deployed session; the loopback check alone would install a puppet in every developer's ordinary
 * browsing of the app, where a real camera is the thing being tested.
 */
export function syntheticPoseDriverEnabled(input: SyntheticPoseGateInput): boolean {
  if (!LOOPBACK_HOSTS.includes(input.hostname)) return false;
  return new URLSearchParams(input.search).has(SYNTHETIC_POSE_PARAM);
}
